import { AttachmentStrategy } from "@redotech/redo-storefront-common/attachment-strategy";
import { InfoModalVersion } from "@redotech/redo-storefront-common/info-modal-version";
import { InsertionPosition } from "@redotech/redo-storefront-common/insertion-position";
import {
  ImageElementType,
  PlacementContainerType,
  PlacementType,
  PriceLocation,
  WidgetElementType,
} from "@redotech/redo-storefront-common/widget-config";
import { ButtonSettingPerProduct } from "@redotech/redo-storefront-widget-common/widget";
import * as z from "zod";

const TitleSubtitleSchema = z.object({
  titleText: z.string(),
  subtitleText: z.string(),
});

const CoverageTextSchema = z.object({
  returns: TitleSubtitleSchema,
  packageProtection: TitleSubtitleSchema,
  both: TitleSubtitleSchema,
});

const PlacementDescriptorSchema = z.object({
  cssSelector: z.string().trim(),
  insertionPosition: z.nativeEnum(InsertionPosition),
  placementContainerType: z.nativeEnum(PlacementContainerType),
  hidingElementSelector: z.string(),
  shadowRootSelector: z.string(),
});

const CommonWidgetFields = {
  placementSelector: z.string().trim(),
  insertionPosition: z.nativeEnum(InsertionPosition),
  shadowRootSelector: z.string(),
  placeInsideAndOutsideShadowRoot: z.boolean(),
  dataTarget: z.string(),
  placements: z.array(PlacementDescriptorSchema),
};

const ClickStandardCheckoutButtonSchema = z.object({
  enabled: z.boolean(),
  checkoutButtonSelector: z.string(),
});

const InfoCardSchema = z.object({
  ...CommonWidgetFields,
  cardText: CoverageTextSchema,
  showInfoIcon: z.boolean(),
  infoCardStyles: z.object({
    color: z.string(),
    titleFontSize: z.number(),
    titleFontColor: z.string(),
    subtitleFontSize: z.number(),
    subtitleFontColor: z.string(),
  }),
  badgeWord: z.string(),
  imageElement: z.nativeEnum(ImageElementType),
  priceLocation: z.nativeEnum(PriceLocation),
  showBackground: z.boolean(),
});

const BothCheckoutButtonsSchema = z.object({
  ...CommonWidgetFields,
  coverageButtonText: CoverageTextSchema,
  nonCoverageButtonText: z.object({
    returns: z.string(),
    packageProtection: z.string(),
    both: z.string(),
  }),
  coverageButtonStyles: z.object({
    color: z.string(),
    fontColor: z.string(),
    subtitleFontColor: z.string(),
    fontSize: z.number(),
    subtitleFontSize: z.number(),
    nonCoverageFontColor: z.string(),
    nonCoverageFontSize: z.number(),
    includeNonCoverageUnderline: z.boolean(),
  }),
  buttonSettingPerProduct: z.nativeEnum(ButtonSettingPerProduct),
  clickStandardCheckoutButton: ClickStandardCheckoutButtonSchema,
});

const IndividualCheckoutButtonSchema = z.object({
  ...CommonWidgetFields,
  content: z.string(),
  clickStandardCheckoutButton: ClickStandardCheckoutButtonSchema,
});

const WidgetConfigItemSchema = z.object({
  type: z.nativeEnum(WidgetElementType),
  name: z.string().optional(),
  placementType: z.nativeEnum(PlacementType),
  config: z.object({
    infoCardConfig: InfoCardSchema.optional(),
    bothCheckoutButtonsConfig: BothCheckoutButtonsSchema.optional(),
    individualCheckoutButtonConfig: IndividualCheckoutButtonSchema.optional(),
  }),
});

const PersistedWidgetConfigItemSchema = WidgetConfigItemSchema.superRefine(
  (item, ctx) => {
    const placements =
      item.type === WidgetElementType.INFO_CARD
        ? item.config.infoCardConfig?.placements
        : item.type === WidgetElementType.BOTH_CHECKOUT_BUTTONS
          ? item.config.bothCheckoutButtonsConfig?.placements
          : item.type === WidgetElementType.COVERAGE_CHECKOUT_BUTTON ||
              item.type === WidgetElementType.NON_COVERAGE_CHECKOUT_BUTTON
            ? item.config.individualCheckoutButtonConfig?.placements
            : undefined;
    if (
      placements &&
      placements.length > 0 &&
      placements.every((placement) => placement.cssSelector.length > 0)
    ) {
      return;
    }
    if (
      item.type === WidgetElementType.INFO_CARD ||
      item.type === WidgetElementType.BOTH_CHECKOUT_BUTTONS ||
      item.type === WidgetElementType.COVERAGE_CHECKOUT_BUTTON ||
      item.type === WidgetElementType.NON_COVERAGE_CHECKOUT_BUTTON
    ) {
      ctx.addIssue({
        code: "custom",
        message: "A verified placement selector is required",
        path: ["config"],
      });
    }
  },
);

const ToggleFieldsSchema = z.object({
  titleText: z.string(),
  positiveSubtext: z.string(),
  negativeSubtext: z.string(),
});

const InfoModalSchema = z.object({
  cartIconEnabled: z.boolean(),
  checkoutIconEnabled: z.boolean(),
  enableTopLayerModalApi: z.boolean(),
  logo: z.string(),
  sideImage: z.string(),
  version: z.nativeEnum(InfoModalVersion),
  headerText: z.string(),
  subheaderText: z.string(),
  line1: TitleSubtitleSchema,
  line2: TitleSubtitleSchema,
  line3: TitleSubtitleSchema,
  customCss: z.string(),
});

const ReturnsCoverageBuilderEditableValueSchema = z.object({
  checkbox: z.object({
    enabled: z.boolean(),
    color: z.string(),
    selector: z.string(),
    fontSize: z
      .string()
      .trim()
      .min(1)
      .refine((value) => Number.isFinite(Number(value)), {
        message: "Font size must be numeric",
      }),
    text: z.string(),
    textOverride: z.string(),
    textAfterRedo: z.string(),
    showPrice: z.boolean(),
  }),
  widgetConfigs: z.array(WidgetConfigItemSchema),
  cartElementImage: z.string().url().optional(),
  useShadowRootInCart: z.boolean(),
  cartShadowRootSelector: z.string(),
  toggleTextOptions: z.object({ returnToggle: ToggleFieldsSchema }),
  toggleOnColor: z.string(),
  toggleOffColor: z.string(),
  toggleCircleColor: z.string(),
  cartToggleSelector: z.string(),
  cartToggleShadowRootSelector: z.string(),
  cartTogglePlacement: z.nativeEnum(InsertionPosition),
  infoModal: InfoModalSchema,
});

export const ReturnsCoverageBuilderInitialValueSchema =
  ReturnsCoverageBuilderEditableValueSchema.extend({
    attachmentStrategy: z.nativeEnum(AttachmentStrategy),
  });

export const ReturnsCoverageBuilderSaveValueSchema =
  ReturnsCoverageBuilderEditableValueSchema.extend({
    widgetConfigs: z.array(PersistedWidgetConfigItemSchema),
  });

export const saveReturnsCoverageBuilder = {
  name: "saveReturnsCoverageBuilder" as const,
  input: ReturnsCoverageBuilderSaveValueSchema,
  output: z.void(),
};
